/**
 * Friendship Verification & Bot Management API Client for frontend-v2
 * Consumes FastAPI endpoints:
 * - POST /api/check/lookup-and-status
 * - POST /api/check/send-all-bot-friend-requests
 * - POST /api/check/notify-shortage
 * Compliant with Milestone M4 & interface contracts in PROJECT.md.
 */

export type EpicPlatform = 'epic' | 'psn' | 'xbl' | 'switch' | 'profile' | string;

export interface EpicUserInfo {
  display_name: string;
  account_id: string;
  source?: string;
  platform: EpicPlatform;
  platform_label: string;
  external_display_name?: string | null;
  searched_name: string;
}

export interface BotAssignment {
  item: {
    offer_id: string;
    name: string;
    vbucks: number;
  };
  bot_id: number;
  bot_name?: string;
}

export type FriendshipStatus =
  | 'FRIENDS_READY'
  | 'FRIENDS_WAITING_48H'
  | 'NOT_FRIENDS'
  | 'REQUEST_SENT'
  | 'NO_BOT_BALANCE'
  | 'NO_BOT_ITEM_BALANCE'
  | 'BOT_LIMIT_REACHED'
  | 'RECHARGE_ONLY';

export interface FriendshipCheckItem {
  offer_id: string;
  name: string;
  vbucks: number;
}

export interface FriendshipCheckRequest {
  username: string;
  account_id?: string | null;
  total_vbucks: number;
  items?: FriendshipCheckItem[];
}

export interface FriendshipCheckResponse {
  eligible: boolean;
  status: FriendshipStatus;
  user?: EpicUserInfo;
  bot_id?: number;
  assignments?: BotAssignment[];
  hours_remaining?: number;
  available_gifts?: number;
  needed_gifts?: number;
  available_vbucks?: number;
  needed_vbucks?: number;
  total_bot_vbucks?: number;
  total_bot_gifts?: number;
  unadded_bots_count?: number;
  pending_bots_count?: number;
  accepted_bots_count?: number;
  bots_ready_count?: number;
  bots_waiting_count?: number;
  can_invite_more?: boolean;
  privacy_blocked?: boolean;
  message?: string;
  guide?: string[];
}

export interface SendBotFriendRequestsPayload {
  username: string;
  account_id: string;
}

export interface SendBotFriendRequestsResponse {
  success: boolean;
  privacy_blocked: boolean;
  sent_bots: string[];
  already_friends_bots: string[];
  total_active_bots?: number;
  message: string;
  guide?: string[];
}

export interface NotifyShortageRequest {
  username: string;
  account_id?: string | null;
  needed_vbucks: number;
  available_vbucks: number;
  needed_gifts: number;
  available_gifts: number;
  items?: FriendshipCheckItem[];
}

export interface CheckApiOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}

export class CheckApiError extends Error {
  statusCode?: number;
  isNotFound?: boolean;
  isRateLimited?: boolean;
  isPrivacyBlocked?: boolean;
  guide?: string[];

  constructor(
    message: string,
    options?: {
      statusCode?: number;
      isNotFound?: boolean;
      isRateLimited?: boolean;
      isPrivacyBlocked?: boolean;
      guide?: string[];
    }
  ) {
    super(message);
    this.name = 'CheckApiError';
    this.statusCode = options?.statusCode;
    this.isNotFound = options?.isNotFound;
    this.isRateLimited = options?.isRateLimited;
    this.isPrivacyBlocked = options?.isPrivacyBlocked;
    this.guide = options?.guide;
  }
}

/** Permanent storage keys for client persistence */
export const EPIC_USERNAME_STORAGE_KEY = 'fn_epic_username';
export const EPIC_ACCOUNT_STORAGE_KEY = 'fn_epic_account';

/** Helper to identify direct recharge offers (bypassing bot friendship checks) */
export function isRechargeItem(offerId?: string): boolean {
  if (!offerId) return false;
  return (
    offerId === 'recharge_vbucks_12500' ||
    offerId === 'recharge_crew' ||
    offerId.startsWith('recharge_')
  );
}

/** Check if the entire items list consists strictly of recharge items */
export function isCartRechargeOnly(
  items: Array<{ offer_id?: string; offerId?: string }>
): boolean {
  if (!items || items.length === 0) return false;
  return items.every((it) => isRechargeItem(it.offer_id || it.offerId));
}

/** Read saved Epic username from localStorage */
export function getSavedEpicUsername(): string {
  try {
    return localStorage.getItem(EPIC_USERNAME_STORAGE_KEY)?.trim() || '';
  } catch {
    return '';
  }
}

/** Save Epic username to localStorage */
export function saveEpicUsername(username: string): void {
  try {
    const clean = username.trim();
    if (clean) {
      localStorage.setItem(EPIC_USERNAME_STORAGE_KEY, clean);
    } else {
      localStorage.removeItem(EPIC_USERNAME_STORAGE_KEY);
    }
  } catch {
    // Ignore storage quota or permission errors
  }
}

/** Read saved Epic account object from localStorage */
export function getSavedEpicAccount(): EpicUserInfo | null {
  try {
    const raw = localStorage.getItem(EPIC_ACCOUNT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as EpicUserInfo;
  } catch {
    return null;
  }
}

/** Save Epic account object to localStorage */
export function saveEpicAccount(account: EpicUserInfo): void {
  try {
    localStorage.setItem(EPIC_ACCOUNT_STORAGE_KEY, JSON.stringify(account));
  } catch {
    // Ignore storage quota or permission errors
  }
}

/** Clear saved Epic credentials */
export function clearSavedEpicAccount(): void {
  try {
    localStorage.removeItem(EPIC_USERNAME_STORAGE_KEY);
    localStorage.removeItem(EPIC_ACCOUNT_STORAGE_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Validates Epic Games username and verifies 48-hour friendship against bot pool.
 * Endpoint: POST /api/check/lookup-and-status
 */
export async function checkFriendshipStatus(
  payload: FriendshipCheckRequest,
  options: CheckApiOptions = {}
): Promise<FriendshipCheckResponse> {
  const cleanUsername = payload.username.trim();
  if (!cleanUsername || cleanUsername.length < 3) {
    throw new CheckApiError('El nombre de usuario debe tener al menos 3 caracteres.', {
      statusCode: 422,
    });
  }

  // Handle direct recharge bypass: if all items are recharge items, bypass bot check
  if (payload.items && payload.items.length > 0 && isCartRechargeOnly(payload.items)) {
    const accountInfo: EpicUserInfo = {
      display_name: cleanUsername,
      account_id: payload.account_id || `recharge_${cleanUsername.toLowerCase()}`,
      platform: 'epic',
      platform_label: 'Recarga Directa',
      searched_name: cleanUsername,
    };
    saveEpicUsername(cleanUsername);
    saveEpicAccount(accountInfo);

    return {
      eligible: true,
      status: 'RECHARGE_ONLY',
      user: accountInfo,
      message:
        'Recarga Directa: Tu pedido no requiere 48 hs de amistad con bots. La carga se coordina por WhatsApp tras el pago.',
    };
  }

  const timeoutMs = options.timeoutMs ?? 12000;
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs);

  const combinedSignal = options.signal || timeoutController.signal;
  if (options.signal) {
    options.signal.addEventListener('abort', () => timeoutController.abort(), { once: true });
  }

  try {
    const bodyPayload = {
      username: cleanUsername,
      account_id: payload.account_id || null,
      total_vbucks: Math.max(0, payload.total_vbucks),
      items: payload.items || [],
    };

    const response = await fetch('/api/check/lookup-and-status', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(bodyPayload),
      signal: combinedSignal,
    });

    clearTimeout(timeoutId);

    if (response.status === 404) {
      const errData = await response.json().catch(() => ({}));
      const msg =
        errData.detail ||
        `No se encontró ningún jugador con el nombre '${cleanUsername}' en Epic Games, PlayStation ni Xbox.`;
      throw new CheckApiError(msg, { statusCode: 404, isNotFound: true });
    }

    if (response.status === 429) {
      throw new CheckApiError(
        'Límite de consultas de verificación excedido. Por favor espera unos momentos antes de reintentar.',
        { statusCode: 429, isRateLimited: true }
      );
    }

    if (response.status === 422) {
      const errData = await response.json().catch(() => ({}));
      throw new CheckApiError(
        errData.detail || 'Formato de usuario inválido (debe tener entre 3 y 50 caracteres).',
        { statusCode: 422 }
      );
    }

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new CheckApiError(
        errData.detail || `Error en el servidor de verificación (código HTTP ${response.status}).`,
        { statusCode: response.status }
      );
    }

    const data: FriendshipCheckResponse = await response.json();

    // Persist verified user account on success
    if (data.user) {
      saveEpicUsername(data.user.display_name || cleanUsername);
      saveEpicAccount(data.user);
    }

    return data;
  } catch (err: any) {
    clearTimeout(timeoutId);

    if (err instanceof CheckApiError) {
      throw err;
    }

    if (err.name === 'AbortError') {
      throw new CheckApiError('La verificación fue cancelada o superó el tiempo de espera.', {
        statusCode: 408,
      });
    }

    throw new CheckApiError(
      err.message || 'No se pudo conectar con el servicio de verificación de Epic Games.',
      { statusCode: 500 }
    );
  }
}

/**
 * Triggers outgoing friend requests from all active bot accounts to the target user.
 * Endpoint: POST /api/check/send-all-bot-friend-requests
 */
export async function sendBotFriendRequests(
  username: string,
  accountId: string,
  options: CheckApiOptions = {}
): Promise<SendBotFriendRequestsResponse> {
  const cleanUsername = username.trim();
  const cleanAccountId = accountId.trim();

  if (!cleanUsername || !cleanAccountId) {
    throw new CheckApiError('Se requiere nombre de usuario y cuenta de Epic para enviar solicitudes.', {
      statusCode: 422,
    });
  }

  const timeoutMs = options.timeoutMs ?? 15000;
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs);

  const combinedSignal = options.signal || timeoutController.signal;

  try {
    const response = await fetch('/api/check/send-all-bot-friend-requests', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        username: cleanUsername,
        account_id: cleanAccountId,
      }),
      signal: combinedSignal,
    });

    clearTimeout(timeoutId);

    if (response.status === 429) {
      throw new CheckApiError(
        'Límite de solicitudes de amistad alcanzado (máximo 5 por minuto). Espera un momento antes de volver a solicitar.',
        { statusCode: 429, isRateLimited: true }
      );
    }

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new CheckApiError(
        errData.detail || 'Error al enviar solicitudes de amistad desde los bots.',
        { statusCode: response.status }
      );
    }

    const data: SendBotFriendRequestsResponse = await response.json();
    return data;
  } catch (err: any) {
    clearTimeout(timeoutId);

    if (err instanceof CheckApiError) {
      throw err;
    }

    if (err.name === 'AbortError') {
      throw new CheckApiError('La petición de solicitud de amistad tardó demasiado tiempo.', {
        statusCode: 408,
      });
    }

    throw new CheckApiError(
      err.message || 'No fue posible comunicar con los bots oficiales de la tienda.',
      { statusCode: 500 }
    );
  }
}

/**
 * Sends a stock or gift shortage alert to the store administrator on Telegram.
 * Endpoint: POST /api/check/notify-shortage
 */
export async function notifyShortage(
  payload: NotifyShortageRequest
): Promise<{ success: boolean; message: string }> {
  try {
    const response = await fetch('/api/check/notify-shortage', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      return { success: false, message: 'No se pudo enviar la alerta de stock.' };
    }

    return await response.json();
  } catch {
    return { success: false, message: 'Error de conexión con el servicio de alertas.' };
  }
}

export default {
  checkFriendshipStatus,
  sendBotFriendRequests,
  notifyShortage,
  getSavedEpicUsername,
  saveEpicUsername,
  getSavedEpicAccount,
  saveEpicAccount,
  clearSavedEpicAccount,
  isRechargeItem,
  isCartRechargeOnly,
};
